import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { generatePath, Link } from "react-router-dom";

import { MiniTag, Typography } from "@/components/Elements";
import { formatDate, stringToDate } from "@/components/helpers/DateFormatters";
import UsersCircle from "@/components/table/shared/UsersCircle";
import { useMainStore } from "@/contexts/Store";
import { RISK_RATING_COLORS, ROUTES } from "@/features/risk-assessment";
import RowOptions from "@/features/risk-assessment/components/RowOptions/RowOptions";
import ActionCell from "@/features/risk-assessment/components/Table/GenericTable/Cells/ActionCell";
import ContentCell from "@/features/risk-assessment/components/Table/GenericTable/Cells/ContentCell";
import ListCell from "@/features/risk-assessment/components/Table/GenericTable/Cells/ListCell/ListCell";
import TableRow from "@/features/risk-assessment/components/Table/GenericTable/TableRow";

import { RiskMethodologyAPI } from "../../../../api/legacy/risk-assessment/RiskMethodologiesApi";
import { RiskMethodology } from "../../types/risk-methodology";

type Props = {
  index: number;
  riskMethodology: RiskMethodology;
};

function RiskMethodologyRow(props: Props) {
  const mainStore = useMainStore();
  const { workspaceID } = mainStore.context;

  useEffect(() => {
    if (!workspaceID) {
      return;
    }
    mainStore.users.indexForModuleIfNoUsersPresent(workspaceID);
  }, [props.riskMethodology.last_edited_by_id, workspaceID]);

  // Not under avroschema
  const updatedAt = props.riskMethodology.last_updated_at
    ? formatDate(stringToDate(props.riskMethodology.last_updated_at))
    : "N/A";
  const editedBy = mainStore.users.allUsers.find(
    (u) => u.id == props.riskMethodology.last_edited_by_id,
  );

  const deleteRiskMethodology = async () => {
    try {
      await RiskMethodologyAPI.delete(
        workspaceID!,
        // @ts-expect-error TS(2345) FIXME: Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
        props.riskMethodology.id,
      );
      mainStore.riskMethodologies.delete(
        // @ts-expect-error TS(2345) FIXME: Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
        props.riskMethodology.id,
      );
    } catch (e) {
      // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
      if (e.response.data.errors?.questionnaires?.length > 1) {
        mainStore.toast.setErrorText(
          `The risk methodology is in use: ${
            // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
            e.response.data.errors.questionnaires.join(", ")
          }`,
        );
      } else {
        mainStore.toast.setErrorText("Something went wrong");
      }
    }
  };

  return (
    <TableRow key={props.riskMethodology.id} rowIndex={props.index}>
      <ContentCell
        content={
          <Link
            to={generatePath(ROUTES.METHODOLOGY_EDIT_PATH, {
              riskMethodologyId: props.riskMethodology.id,
              workspace_id: Number(workspaceID),
            })}
          >
            {props.riskMethodology.name}
          </Link>
        }
        isFirstDataCell
      />
      <ListCell>
        {props.riskMethodology.overall_risk_ratings.map((rating) => (
          <MiniTag
            key={rating.id}
            label={
              <Typography label={rating.text} color="generalWhite" size="sm" />
            }
            style={{
              backgroundColor:
                rating.color ||
                RISK_RATING_COLORS[
                  Math.floor(Math.random() * RISK_RATING_COLORS.length)
                ],
            }}
          />
        ))}
      </ListCell>
      <ListCell>
        {props.riskMethodology.inherent_risk_ratings.map((rating) => (
          <MiniTag
            key={rating.id}
            label={
              <Typography label={rating.text} color="generalWhite" size="sm" />
            }
            style={{
              backgroundColor:
                rating.color ||
                RISK_RATING_COLORS[
                  Math.floor(Math.random() * RISK_RATING_COLORS.length)
                ],
            }}
          />
        ))}
      </ListCell>
      <ListCell>
        {props.riskMethodology.control_risk_ratings.map((rating) => (
          <MiniTag
            key={rating.id}
            label={
              <Typography label={rating.text} color="generalWhite" size="sm" />
            }
            style={{
              backgroundColor:
                rating.color ||
                RISK_RATING_COLORS[
                  Math.floor(Math.random() * RISK_RATING_COLORS.length)
                ],
            }}
          />
        ))}
      </ListCell>
      <ContentCell content={updatedAt} />
      <ContentCell
        content={editedBy ? <UsersCircle user={editedBy} /> : "N/A"}
      />
      <ActionCell>
        <RowOptions
          options={[
            { label: "Delete", onClick: () => deleteRiskMethodology() },
          ]}
        />
      </ActionCell>
    </TableRow>
  );
}

export default observer(RiskMethodologyRow);
